import * as React from "react"
import * as SelectPrimitive from "@radix-ui/react-select"

import { DISABLED, FIELD, FIELD_FOCUS, FIELD_SIZES, FOCUS, HOVER, MENU, MENU_ITEM, fieldLook, type FieldLook, type FieldSize } from "@/components/ui/recipes"
import { cn } from "@/utils/utils"
import { IconButton } from "@/components/ui/icon-button"
import { CaretDownIcon, CaretUpIcon, CheckIcon } from "@phosphor-icons/react"

const Select = SelectPrimitive.Root

const SelectGroup = SelectPrimitive.Group

const SelectValue = SelectPrimitive.Value

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & { size?: FieldSize; look?: FieldLook }
>(({ className, children, size = "md", look, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      FIELD,
      fieldLook(size, look),
      FIELD_SIZES[size],
      FIELD_FOCUS[size],
      "flex items-center justify-between gap-2 text-left hover:border-lureo-brand-ink data-[state=open]:border-lureo-brand-ink data-[placeholder]:text-lureo-ink-subtlest [&>span]:line-clamp-1",
      className
    )}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <CaretDownIcon className="h-4 w-4 shrink-0 text-lureo-ink-subtle" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
))
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName

/** A settings row that opens a select: its name, then the chosen value (SelectValue) and a caret at the row's end (a trigger's Type). */
const SelectRowTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & { label: React.ReactNode }
>(({ className, children, label, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      "flex w-full items-center gap-2.5 px-3 py-2.5 text-left font-lureo-font-ui outline-hidden transition-colors enabled:cursor-pointer",
      HOVER,
      FOCUS,
      DISABLED,
      className
    )}
    {...props}
  >
    <span className="flex-1 text-lureo-13 text-lureo-ink-subtle">{label}</span>
    <span className="text-lureo-13 font-semibold tabular-nums text-lureo-ink">{children}</span>
    <SelectPrimitive.Icon asChild>
      <CaretDownIcon className="h-3 w-3 shrink-0 text-lureo-ink-subtle" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
))
SelectRowTrigger.displayName = "SelectRowTrigger"

/** A select opened from an icon alone, in a header or toolbar: an icon button showing the chosen value's mark, named by `label`. */
const SelectIconTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  Omit<React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>, "asChild"> & { label: string }
>(({ children, label, className, ...props }, ref) => (
  <SelectPrimitive.Trigger ref={ref} asChild {...props}>
    <IconButton label={label} className={className}>
      {children}
    </IconButton>
  </SelectPrimitive.Trigger>
))
SelectIconTrigger.displayName = "SelectIconTrigger"

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn(
      "flex cursor-default items-center justify-center py-1",
      className
    )}
    {...props}
  >
    <CaretUpIcon className="h-4 w-4" />
  </SelectPrimitive.ScrollUpButton>
))
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn(
      "flex cursor-default items-center justify-center py-1",
      className
    )}
    {...props}
  >
    <CaretDownIcon className="h-4 w-4" />
  </SelectPrimitive.ScrollDownButton>
))
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = "popper", ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        MENU,
        "relative max-h-96 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:ease-lureo-easing-enter data-[state=closed]:ease-lureo-easing-exit data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        position === "popper" &&
          "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
        className
      )}
      position={position}
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(
          position === "popper" &&
            "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]"
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
))
SelectContent.displayName = SelectPrimitive.Content.displayName

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn("px-2.5 py-1.5 text-lureo-12 font-semibold uppercase tracking-[0.1em] text-lureo-on-menu-subtle leading-4", className)}
    {...props}
  />
))
SelectLabel.displayName = SelectPrimitive.Label.displayName

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      MENU_ITEM,
      "pl-8",
      className
    )}
    {...props}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <CheckIcon className="h-4 w-4" />
      </SelectPrimitive.ItemIndicator>
    </span>

    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
))
SelectItem.displayName = SelectPrimitive.Item.displayName

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn("-mx-1.5 my-1 h-px bg-lureo-menu-line", className)}
    {...props}
  />
))
SelectSeparator.displayName = SelectPrimitive.Separator.displayName

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectRowTrigger,
  SelectIconTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
}
